import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { MapPin, TrendingUp } from "lucide-react";
import { CardStatusTag, CardSaveButton } from "./CardHeaderActions";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatPriceExact, getPrimaryImg } from "@/utils";
import { RentalProperty } from "@/types";
import { useAuthStore } from "@/store/authStore";
import { PERMISSIONS } from "@/routes/guards";
import { useToggleSaveListing } from "@/hooks/useSavedListings";

type Props = {
  item: RentalProperty;
  isSelected?: boolean;
  onMouseEnter?: () => void;
  layout?: 'vertical' | 'horizontal';
};

export default function RentalCard({ item, isSelected, onMouseEnter, layout = 'vertical' }: Props) {
  const { user } = useAuthStore();
  const { isSaved, toggleSave } = useToggleSaveListing();
  const saved = isSaved(item.id);
  const img = getPrimaryImg(item.images);

  // Chỉ hiển thị tag trạng thái cho Seller/Admin. Buyer và Guest không cần thấy.
  const showStatusTag = !!user && (user.role === 'admin' || user.role === 'landlord');

  if (layout === 'horizontal') {
    return (
      <Link
        to={`/cho-thue/${item.id}`}
        onMouseEnter={onMouseEnter}
        className={cn(
          "fnb-card block animate-fade-up flex flex-row relative transition-all rounded-xl border bg-white overflow-hidden hover:shadow-md h-[136px] shrink-0",
          isSelected ? "ring-2 ring-orange-500 shadow-brand border-orange-400" : "border-gray-200 hover:border-orange-200"
        )}
      >
        {/* Thumbnail */}
        <div className="w-36 sm:w-44 h-full overflow-hidden relative shrink-0 bg-orange-50">
          {img ? (
            <img
              src={img}
              alt={item.title}
              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center text-4xl"
              style={{ background: "var(--brand-pale)" }}
            >
              🏪
            </div>
          )}

          <CardStatusTag
            className="absolute top-2 left-2 z-10 scale-90 origin-top-left"
            show={showStatusTag}
            status={item.approvalStatus}
            label={item.approvalStatus === "approved" ? "Đã duyệt" : item.approvalStatus === "rejected" ? "Từ chối" : "Chờ duyệt"}
          />
        </div>

        {/* Body */}
        <div className="p-3 flex flex-col justify-between flex-1 min-w-0 pr-10">
          <div>
            <div className="flex gap-1 mb-1 flex-wrap">
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-normal">
                {item.propertyType}
              </Badge>
              {item.businessType && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-normal">
                  {item.businessType}
                </Badge>
              )}
            </div>
            <h3 className="font-semibold text-xs leading-snug line-clamp-2 text-gray-900 mb-1">
              {item.title}
            </h3>
          </div>

          <div>
            <p className="font-display font-bold text-sm text-orange-600 mb-1">
              {formatPriceExact(String(item.monthlyRent))} {item.currencyUnit}/tháng
            </p>

            <div className="flex items-center gap-2 text-[11px] text-gray-500 truncate">
              <span className="flex items-center gap-0.5 truncate">
                <MapPin size={10} className="shrink-0 text-gray-400" /> {item.district?.name}, {item.province?.name}
              </span>
              {Number(item.area) > 0 && (
                <span className="shrink-0 font-medium text-gray-600">
                  · {item.area} {item.areaUnit}
                </span>
              )}
            </div>
          </div>
        </div>

        <CardSaveButton
          className="absolute bottom-2.5 right-2.5 z-10 scale-90"
          saved={saved}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleSave(item.id, "Rental");
          }}
        />
      </Link>
    );
  }

  return (
    <Link
      to={`/cho-thue/${item.id}`}
      onMouseEnter={onMouseEnter}
      className={cn(
        "fnb-card block animate-fade-up h-full flex flex-col relative transition-all",
        isSelected && "ring-2 ring-orange-500 shadow-brand"
      )}
    >
      {/* Thumbnail */}
      <div className="h-44 overflow-hidden rounded-t-xl relative shrink-0">
        {img ? (
          <img
            src={img}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-6xl"
            style={{ background: "var(--brand-pale)" }}
          >
            🏪
          </div>
        )}

        <CardStatusTag
          className="absolute top-2 left-2 z-10"
          show={showStatusTag}
          status={item.approvalStatus}
          label={item.approvalStatus === "approved" ? "Đã duyệt" : item.approvalStatus === "rejected" ? "Từ chối" : "Chờ duyệt"}
        />
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        <h3
          className="font-semibold text-sm leading-snug line-clamp-2 mb-3 pr-8"
          style={{ color: "var(--text-1)" }}
        >
          {item.title}
        </h3>

        {/* Price */}
        <p
          className="font-display font-black text-lg mb-3"
          style={{ color: "var(--brand)" }}
        >
          {formatPriceExact(String(item.monthlyRent))} {item.currencyUnit}/tháng
        </p>

        {/* Type badges */}
        <div className="flex gap-1.5 mb-3 flex-wrap">
          <Badge variant="secondary" className="text-[10px]">
            {item.propertyType}
          </Badge>
          <Badge variant="secondary" className="text-[10px]">
            {item.businessType}
          </Badge>
        </div>

        {/* Meta */}
        <div
          className="flex flex-wrap gap-1.5 pt-3 border-t mt-auto pr-10"
          style={{ borderColor: "var(--border-c)" }}
        >
          <span className="fnb-tag text-[11px]">
            <MapPin size={10} /> {item.district?.name}, {item.province?.name}
          </span>

          {Number(item.area) > 0 && (
            <span className="fnb-tag text-[11px]">
              {item.area} {item.areaUnit}
            </span>
          )}
        </div>
      </div>

      <CardSaveButton
        className="absolute bottom-4 right-4 z-10"
        saved={saved}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleSave(item.id, "Rental");
        }}
      />
    </Link>
  );
}

